/* eslint-disable tss-unused-classes/unused-classes */
import { Chip, Grid, IconButton } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useTagStyles } from './TagsWrapperstyles';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomTooltip } from 'app/components/Common/Tooltips';
import { ISelection } from 'app/ComponentLibrary/Filter/TypeAheadDropdown/CustomTypeAhead';
import CloseIcon from '@mui/icons-material/Close';
import { AddTagsIcon } from './AddTagsIcon';
import lookupTags from 'app/assets/jsons/TagsV2.json';
import { CandidateTagHistoryModal } from './CandidateTagHistoryModal';
import {
  getPreservedCandidateTags,
  mapCandidateTagToChip,
  mapTagTypeToLookupOption,
  matchesCandidateTag,
  shouldDisplayCandidateTag,
} from './tagTypeHelpers';
import { useDispatch, useSelector } from 'react-redux';
import {
  candidateDetailsSelection,
  selectTagTypes,
  selectTags,
} from '../../Profile/CandidateDetails.selector';
import { Authorized } from 'oidc/userHelper';
import { useReadOnly, userRoles } from 'oidc/userRoles';
import { selectUser } from 'oidc/user.selectors';
import { candidateDetailActions } from '../../Profile/CandidateDetails.redux';
import { createLabelValue, shortenText, getWindowTagCount } from './TagUtils';
import { useTranslation } from 'react-i18next';
import { useDecision } from '@optimizely/react-sdk';
import { ff_candidate_tags_migrate_to_sql_table } from 'app/constants/FeatureFlags/Candidate/Keys';

export enum TagValues {
  SUPERVISOR = 'Supervisor',
  PRECHECK = 'Pre-Check',
  PROFILEREADY = 'Profile Ready',
}

export const TagsWrapper = props => {
  const { buttonId, candidateDetails } = props;
  const { t } = useTranslation();
  const [migrateTagsToSqlTable] = useDecision(ff_candidate_tags_migrate_to_sql_table);
  const useSqlTagTypes = migrateTagsToSqlTable?.enabled;
  const anchorAccessorId = buttonId || 'tags-anchor-accessor';
  const { classes: tagClasses } = useTagStyles();
  const user = useSelector(selectUser);
  const tags = useSelector(selectTags);
  const tagTypes = useSelector(selectTagTypes);
  const [tagsList, setTagsList] = useState<ISelection[]>([]);
  const [options, setOptions] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [isRecruiter, setIsRecruiter] = useState<boolean>(false);
  const candidateInfo = useSelector(candidateDetailsSelection);
  const dispatch = useDispatch();
  const { readOnly } = useReadOnly();
  const [tagDisplayCount, setTagDisplayCount] = useState(0);
  const [tagHistoryOpen, setTagHistoryOpen] = useState(false);

  useEffect(() => {
    setIsRecruiter(
      Authorized(
        [userRoles.recruitment_TeamMember, userRoles.recruitment_Leadership],
        user?.userInfo,
      ),
    );
  }, [user?.userInfo]);

  useEffect(() => {
    const handleWindowResize = () => {
      setTagDisplayCount(getWindowTagCount());
    };
    window.addEventListener('resize', handleWindowResize);
    setTagDisplayCount(getWindowTagCount());
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const jsonLookupOptions = useMemo(
    () =>
      lookupTags.map(e => ({
        ...e,
        name: e.label,
        key: e.id,
        disabled: false,
      })),
    [],
  );

  const apiLookupOptions = useMemo(() => {
    if (!tagTypes?.length) {
      return [];
    }
    return tagTypes.map(mapTagTypeToLookupOption);
  }, [tagTypes]);

  const lookupOptions = useSqlTagTypes ? apiLookupOptions : jsonLookupOptions;

  useEffect(() => {
    setOptions(lookupOptions);
    setTagsOptions(
      useSqlTagTypes
        ? lookupOptions.filter(e => !e.isHidden && shouldDisplayCandidateTag(e))
        : lookupOptions.filter(e => !e.isHidden),
    );
  }, [lookupOptions, useSqlTagTypes]);

  const preservedCandidateTags = useMemo(
    () => (useSqlTagTypes ? getPreservedCandidateTags(tags) : []),
    [tags, useSqlTagTypes],
  );

  useEffect(() => {
    if (!tags) {
      return;
    }

    if (useSqlTagTypes) {
      if (Array.isArray(tags)) {
        const filterTags = tags
          .filter(shouldDisplayCandidateTag)
          .map(apiTag => {
            const lookupMatch = lookupOptions.find(lookupTagItem => matchesCandidateTag(apiTag, lookupTagItem));
            return mapCandidateTagToChip(apiTag, lookupMatch);
          });
        setTagsList(filterTags);
      }
    } else if (Array.isArray(tags)) {
      const filterTags = lookupOptions
        ?.filter(lookupTagItem => tags.find(tag => tag.value === lookupTagItem.value))
        ?.map(e => ({ ...e, name: e.label }));
      setTagsList(filterTags);
    }
  }, [tags, lookupOptions, tagDisplayCount, useSqlTagTypes]);

  const handleDeleteTag = filteredTags => {
    const postReqBody = {
      candidateId: candidateDetails?.travelerId || candidateInfo?.travelerId,
      brandId: candidateDetails?.brandId || candidateInfo?.brandId,
      currentEmployeeId: user.userInfo.employeeId,
      currentEmployeeName: user.userInfo.firstName + ' ' + user.userInfo.lastName,
      candidateTags: useSqlTagTypes ? [...filteredTags, ...preservedCandidateTags] : filteredTags,
      migrateToSqlTable: useSqlTagTypes,
    };

    dispatch(candidateDetailActions.postTags(postReqBody));
  };

  /**
   * Method to delete tag item
   * @param tagItem
   */
  const handleDelete = (tagItem: ISelection) => {
    const filteredTags = tagsList.filter(currentItem => currentItem.id !== tagItem?.id);
    handleDeleteTag(filteredTags);
    setTagsList(filteredTags);
  };

  const canDelete = (label: string) => {
    return label !== TagValues.SUPERVISOR && label !== TagValues.PRECHECK && label !== TagValues.PROFILEREADY;
  };

  const getChipTooltipContent = (item: ISelection) => {
    const fullDescription = item?.fullDescription?.trim();
    if (fullDescription) {
      return fullDescription;
    }
    if (item?.value?.length > 10) {
      return item?.name;
    }
    return null;
  };

  const wrapChipWithTooltip = (chip: React.ReactNode, item: ISelection) => {
    const tooltipContent = getChipTooltipContent(item);
    if (!tooltipContent) {
      return chip;
    }
    return (
      <CustomTooltip
        contentStyle={{ fontWeight: 400 }}
        tooltipContent={tooltipContent}
        placement="bottom-start"
        isInlineContent={true}
        isStandardContent={!!item?.fullDescription?.trim()}
      >
        {chip}
      </CustomTooltip>
    );
  };

  const renderTagChip = (item: ISelection, index: number) => {
    const label = createLabelValue(item);
    const chip = (
      <Chip
        id={`tags-candidate-header-${index}`}
        key={index}
        className={tagClasses.chip}
        label={
          <>
            <div id={'fullText'}>{label}</div>
            <div id={'shortenedText'}>{item?.name !== TagValues.PROFILEREADY ? shortenText(label) : label}</div>
          </>
        }
        onDelete={canDelete(item?.name) ? () => handleDelete(item) : undefined}
        deleteIcon={
          canDelete(item?.name) && !readOnly ? (
            <CloseIcon style={{ width: '15px', height: '15px', color: 'white' }} />
          ) : undefined
        }
        disabled={readOnly}
      />
    );

    return (
      <Grid item key={index} className={tagClasses.gridItemStyle}>
        {wrapChipWithTooltip(chip, item)}
      </Grid>
    );
  };

  const Chips = useMemo(() => {
    const chips = [];
    tagsList?.slice(0, tagDisplayCount).map((item: ISelection, index: number) => {
      chips.push(renderTagChip(item, index));
    });
    if (tagsList?.length > tagDisplayCount) {
      const remainingChips = tagsList
        .slice(tagDisplayCount)
        .map((item, index) =>
          wrapChipWithTooltip(
            <Chip
              id={`tags-candidate-header-overflow-${index}`}
              key={index}
              className={tagClasses.chip}
              label={<div id={'fullText'}>{item.name}</div>}
              onDelete={canDelete(item.name) ? () => handleDelete(item) : undefined}
              deleteIcon={
                canDelete(item.name) ? <CloseIcon style={{ width: '15px', height: '15px', color: 'white' }} /> : undefined
              }
              disabled={readOnly}
            />,
            item,
          ),
        );

      chips.push(
        <Grid item className={tagClasses.gridItemStyle}>
          <CustomTooltip
            tooltipContent={
              <Grid container direction="row">
                {remainingChips.map((item, index) => {
                  return (
                    <Grid item key={index} style={{ padding: '5px 0px' }}>
                      {item}
                    </Grid>
                  );
                })}
              </Grid>
            }
            interactive
            arrow
            placement="bottom-start"
          >
            <Chip variant="additionalchip" label={`+${remainingChips.length}`} />
          </CustomTooltip>
        </Grid>,
      );
      return chips;
    } else {
      return chips;
    }
  }, [tagsList, tagDisplayCount, readOnly, tagClasses]);

  return (
    <Grid container direction="row" className={tagClasses.container}>
      {Chips}
      <Grid item className={`${tagClasses.gridItemStyle} ${tagClasses.addIconStyle} ${tagClasses.tagActionsStyle}`}>
        <AddTagsIcon
          anchorAccessorId={anchorAccessorId}
          tagsOptions={tagsOptions}
          tagsList={tagsList}
          setTagsList={setTagsList}
          preservedCandidateTags={useSqlTagTypes ? preservedCandidateTags : []}
          migrateToSqlTable={useSqlTagTypes}
          isRecruiter={isRecruiter}
          candidateDetails={candidateDetails}
        />
        {useSqlTagTypes && (
          <div className={tagClasses.tagIconWrap}>
            <CustomTooltip
              tooltipContent={t('candidate.candidateHeader.tagHistory')}
              placement="bottom-start"
              isInlineContent={true}
            >
              <IconButton
                color="secondary"
                size="medium"
                onClick={() => setTagHistoryOpen(true)}
                aria-label={t('candidate.candidateHeader.tagHistory')}
              >
                <HistoryIcon />
              </IconButton>
            </CustomTooltip>
          </div>
        )}
      </Grid>
      {useSqlTagTypes && (
        <CandidateTagHistoryModal
          open={tagHistoryOpen}
          onClose={() => setTagHistoryOpen(false)}
          candidateId={candidateDetails?.travelerId || candidateInfo?.travelerId}
          brandId={candidateDetails?.brandId || candidateInfo?.brandId}
        />
      )}
    </Grid>
  );
};
